import { Stack } from '@mui/material'
import { ColumnDef } from '@tanstack/react-table'
import { Percentage, tokens } from 'ComponentLibrary'
import type { MonetaryValueDataObjectType } from 'ComponentLibrary/src/common/money'
import type { TenantUsageTypeLabelKeys } from 'ComponentLibrary/src/common/tenant'
import { ContentChip } from 'ComponentLibrary/src/piazza/content'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { SimpleTable } from 'ComponentLibrary/src/piazza/tables/SimpleTable/SimpleTable'
import { SimpleTableCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableCell'
import { SimpleTableHeadline } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableHeadline'
import {
    SimpleTableMonetaryCell,
    SimpleTableNumberCell,
    SimpleTablePercentageCell,
    SimpleTableTextCell,
} from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTextCells'

type MarketDataMultiplierValue = {
    base_value?: number
    current_value?: number
    difference?: number
}

type MarketDataMultiplier = {
    multiplier_type?: 'multiplier' | 'discount_rate'
    minimum?: MarketDataMultiplierValue
    average?: MarketDataMultiplierValue
    maximum?: MarketDataMultiplierValue
}

type MarketDataRentValue = {
    base_value?: MonetaryValueDataObjectType
    current_value?: MonetaryValueDataObjectType
    difference?: number
}

type MarketDataRent = {
    usage_type: TenantUsageTypeLabelKeys
    minimum?: MarketDataRentValue
    average?: MarketDataRentValue
    maximum?: MarketDataRentValue
}

type ValueCheckMarketData = {
    rents?: MarketDataRent[]
    multiplier?: MarketDataMultiplier
}

type ValueCheckMarketDataWidgetProps = {
    marketData?: ValueCheckMarketData
    copy: ValueCheckMarketDataWidgetCopy
    locale?: string
}

export type ValueCheckMarketDataWidgetCopy = {
    title: string
    sectionLabels: {
        multiplier: string
        rents: string
    }
    rowLabels: {
        minimum: string
        average: string
        maximum: string
    }
    columnLabels: {
        previousValuation: string
        valueCheck: string
        change: string
    }
    usageTypeLabels: Record<TenantUsageTypeLabelKeys, string>
}

type MarketDataValueType = 'monetary' | 'number' | 'percentage'

type MarketDataRow = {
    id: string
    label: string
    labelVariant: 'section' | 'item'
    valueType?: MarketDataValueType
    baseValue?: MonetaryValueDataObjectType | number
    currentValue?: MonetaryValueDataObjectType | number
    difference?: number
}

const renderValueCell = (valueType?: MarketDataValueType, value?: MarketDataRow['baseValue'], locale = 'de-DE') => {
    const monetaryValue = typeof value === 'number' ? undefined : value
    const numericValue = typeof value === 'number' ? value : undefined
    switch (valueType) {
        case 'monetary':
            return <SimpleTableMonetaryCell monetaryValue={monetaryValue} alignment="right" locale={locale} />
        case 'percentage':
            return <SimpleTablePercentageCell percentage={numericValue} alignment="right" locale={locale} />
        case 'number':
            return <SimpleTableNumberCell number={numericValue} numberOfDigits={2} alignment="right" locale={locale} />
        case undefined:
            return <SimpleTableCell alignment="right" />
    }
}

export default function ValueCheckMarketDataWidget({ marketData, copy, locale = 'de-DE' }: ValueCheckMarketDataWidgetProps) {
    const rents = marketData?.rents || []
    const shouldShowRentUsageType = rents.length > 1
    const rentRows: MarketDataRow[] = []
    rents.forEach((rent, index) => {
        const rentSectionLabel = shouldShowRentUsageType
            ? `${copy.sectionLabels.rents} - ${copy.usageTypeLabels[rent.usage_type]}`
            : copy.sectionLabels.rents

        rentRows.push(
            { id: `rents-section-${index}`, label: rentSectionLabel, labelVariant: 'section' },
            {
                id: `rents-minimum-${index}`,
                label: copy.rowLabels.minimum,
                labelVariant: 'item',
                valueType: 'monetary',
                baseValue: rent.minimum?.base_value,
                currentValue: rent.minimum?.current_value,
                difference: rent.minimum?.difference,
            },
            {
                id: `rents-average-${index}`,
                label: copy.rowLabels.average,
                labelVariant: 'item',
                valueType: 'monetary',
                baseValue: rent.average?.base_value,
                currentValue: rent.average?.current_value,
                difference: rent.average?.difference,
            },
            {
                id: `rents-maximum-${index}`,
                label: copy.rowLabels.maximum,
                labelVariant: 'item',
                valueType: 'monetary',
                baseValue: rent.maximum?.base_value,
                currentValue: rent.maximum?.current_value,
                difference: rent.maximum?.difference,
            }
        )
    })
    const multiplier = marketData?.multiplier
    const multiplierValueType: MarketDataValueType = multiplier?.multiplier_type === 'discount_rate' ? 'percentage' : 'number'

    const rows: MarketDataRow[] = [
        { id: 'multiplier-section', label: copy.sectionLabels.multiplier, labelVariant: 'section' },
        {
            id: 'multiplier-minimum',
            label: copy.rowLabels.minimum,
            labelVariant: 'item',
            valueType: multiplierValueType,
            baseValue: multiplier?.minimum?.base_value,
            currentValue: multiplier?.minimum?.current_value,
            difference: multiplier?.minimum?.difference,
        },
        {
            id: 'multiplier-average',
            label: copy.rowLabels.average,
            labelVariant: 'item',
            valueType: multiplierValueType,
            baseValue: multiplier?.average?.base_value,
            currentValue: multiplier?.average?.current_value,
            difference: multiplier?.average?.difference,
        },
        {
            id: 'multiplier-maximum',
            label: copy.rowLabels.maximum,
            labelVariant: 'item',
            valueType: multiplierValueType,
            baseValue: multiplier?.maximum?.base_value,
            currentValue: multiplier?.maximum?.current_value,
            difference: multiplier?.maximum?.difference,
        },
        ...rentRows,
    ]

    const columns: ColumnDef<MarketDataRow>[] = [
        {
            id: 'label',
            header: () => <SimpleTableCell />,
            cell: ({ row }) =>
                row.original.labelVariant === 'section' ? (
                    <SimpleTableHeadline text={row.original.label} variant="primary" />
                ) : (
                    <SimpleTableTextCell text={row.original.label} variant="lowContrast" />
                ),
        },
        {
            id: 'base-year',
            header: () => <SimpleTableHeadline text={copy.columnLabels.previousValuation} variant="secondary" alignment="right" />,
            cell: ({ row }) =>
                row.original.labelVariant === 'section'
                    ? renderValueCell(undefined, undefined)
                    : renderValueCell(row.original.valueType, row.original.baseValue, locale),
        },
        {
            id: 'current-year',
            header: () => <SimpleTableHeadline text={copy.columnLabels.valueCheck} variant="secondary" alignment="right" />,
            cell: ({ row }) =>
                row.original.labelVariant === 'section'
                    ? renderValueCell(undefined, undefined)
                    : renderValueCell(row.original.valueType, row.original.currentValue, locale),
        },
        {
            id: 'difference',
            header: () => <SimpleTableHeadline text={copy.columnLabels.change} variant="secondary" alignment="right" />,
            cell: ({ row }) => {
                if (row.original.labelVariant === 'section') {
                    return <SimpleTableCell alignment="right" />
                }
                const difference = row.original.difference
                if (difference === undefined) {
                    return <SimpleTableTextCell alignment="right" />
                }
                const chipColor = difference > 0 ? 'green' : difference < 0 ? 'red' : 'blue'
                const formattedDifference = new Percentage(difference).format({
                    numberOfDigits: 2,
                    withSign: true,
                    withSpaceAfterSign: true,
                    locale,
                })
                return (
                    <SimpleTableCell alignment="right">
                        <ContentChip size="small" color={chipColor} label={formattedDifference} />
                    </SimpleTableCell>
                )
            },
        },
    ]

    return (
        <Stack direction="column" gap={tokens.SizingSpacing200}>
            <Headline title={copy.title} variant="headline3" />
            <SimpleTable data={rows} columns={columns} width="100%" backgroundColor={tokens.ColorSurfaceDefault} disableVirtualization />
        </Stack>
    )
}
